/* Types */
import {
  AutocompleteService,
  AutocompleteSuggestion,
} from '@/types/mapServices'
import { StateOfAutocomplete } from '@/constants/components/autocomplete'
import { Suggestion } from '@/types/api/dadata/suggestionAddress'

/* Utils */
import { joinPartsOfAddress } from '@/utils/joinPartsOfAddress'

/* Services */
import dadata from '@/services/autocomplete/dadataApi'

/**
 * Формирование полей адреса в одну строку
 * @param suggestion - данные адреса
 */
export const makeDadataAddressString = (
  suggestion: Suggestion,
): string | null => {
  const addressData = suggestion.data
  const region = addressData.region_with_type
  const settlement = addressData.settlement_with_type
  const city =
    (addressData.city !== addressData.region &&
      joinPartsOfAddress([addressData.city_type, addressData.city], ' ')) ||
    ''
  const street = joinPartsOfAddress(
    [addressData.street_type, addressData.street],
    ' ',
  )
  const house = joinPartsOfAddress(
    [
      addressData.house_type,
      addressData.house,
      addressData.block_type,
      addressData.block,
    ],
    ' ',
  )

  const address =
    joinPartsOfAddress([city, settlement, street, house]) || region

  if (!address) return null

  return address
}

/**
 * Формирование полей адреса в одну короткую строку
 * @param suggestion - данные адреса
 */
export const makeShortDadataAddressString = (
  suggestion: Suggestion,
): string | undefined => {
  const addressData = suggestion.data
  const region = addressData.region_with_type
  const settlement = addressData.settlement_with_type
  const city =
    (addressData.city !== addressData.region &&
      joinPartsOfAddress([addressData.city_type, addressData.city], ' ')) ||
    ''
  const street = joinPartsOfAddress(
    [addressData.street_type, addressData.street],
    ' ',
  )
  const house = joinPartsOfAddress(
    [
      addressData.house_type,
      addressData.house,
      addressData.block_type,
      addressData.block,
    ],
    ' ',
  )

  let address = joinPartsOfAddress([settlement, street, house])

  if (!address) address = city

  if (!address) address = region

  if (!address) return undefined

  return address
}

/**
 * Формируем список найденных адресов api
 * @param dadataSuggestions - список адресов
 */
export const transformDadataAutocomplete = (
  dadataSuggestions: Suggestion[],
): AutocompleteSuggestion[] => {
  const autocompleteList: AutocompleteSuggestion[] = []

  dadataSuggestions.forEach((suggestion) => {
    const address = makeDadataAddressString(suggestion)

    if (address)
      autocompleteList.push({
        value: address,
      })
  })

  if (autocompleteList.length === 0) throw new Error(StateOfAutocomplete.EMPTY)

  return autocompleteList
}

/**
 * Получаем список адресов с api dadata
 * @param apiKey - ключ для запроса dadata
 */
export const makeDadataAutocompleteService = (
  apiKey: string,
): AutocompleteService => {
  const dadataApi = dadata.init(
    'https://suggestions.dadata.ru/suggestions/api/4_1/rs/',
    apiKey,
  )
  async function getSuggestions(
    request: string,
  ): Promise<AutocompleteSuggestion[]> {
    const suggestions = await dadataApi.getSuggestions(request, {
      count: 25,
      bound: 'house',
    })
    return transformDadataAutocomplete(suggestions)
  }

  // @ts-ignore
  return { getSuggestions }
}
